import { CardCvcElement, CardExpiryElement, CardNumberElement } from "@stripe/react-stripe-js";
import StripeOnceOffFlow from "lib/StripeOnceOffFlow";
import StripePaymentIntentManager from "lib/StripePaymentIntentManager";
import StripeRecurringFlow from "lib/StripeRecurringFlow";

/**
 * Co-ordinates Stripe payment flows with the donation form
 */
export default class StripeManager {
  /**
   * Creates a `StripeManager`
   * @param allowed true if Stripe is configured for the current region
   * @param donationForm a React ref to the donation form
   * @param elements a reference to the Stripe Elements object in the Stripe JS SDK
   * - https://stripe.com/docs/stripe-js
   * @param stripe a reference to the Stripe JS SDK - https://stripe.com/docs/stripe-js
   * @param tcDonateGateway an instance of `TcDonateGateway`, used to call
   * the TC-Donate backend
   */
  constructor({ allowed, donationForm, elements, stripe, tcDonateGateway }) {
    this.allowed = allowed;
    this.donationForm = donationForm;
    this.elements = elements;
    this.stripe = stripe;
    this.stripeOnceOffFlow = new StripeOnceOffFlow(this);
    this.stripePaymentIntentManager = new StripePaymentIntentManager({ tcDonateGateway });
    this.stripeRecurringFlow = new StripeRecurringFlow(this);
    this.tcDonateGateway = tcDonateGateway;
    this.confirmCardPayment = this.confirmCardPayment.bind(this);
    this.fail = this.fail.bind(this);
    this.succeed = this.succeed.bind(this);
  }

  /**
   * Indicates whether payment via Stripe is available.
   * @return {boolean} True when both `allowed` and `stripe` are truthy
   */
  get available() {
    return !!(this.allowed && this.stripe);
  }

  /**
   * Internal interface supplying a donor's available contact details to be
   *   passed to Stripe in as `billing_details` to `createPaymentMethod()` in
   *   the format Stripe expects
   * @return {{address: {country: *, city: *, state: *, postal_code: *,
   *   line1: *}, name: string, email: *}}
   * @see https://stripe.com/docs/js/payment_methods/create_payment_method
   * @see https://stripe.com/docs/js/deprecated/handle_card_payment
   */
  get billingDetails() {
    const {
      address: line1,
      city,
      countryCode: country,
      email,
      lastName,
      name: firstName,
      postcode: postal_code,
      state,
    } = this.donationDetails;
    const name = [firstName, lastName].join(" ").trim();

    const address = { city, country, line1, postal_code, state };
    return { address, email, name };
  }

  /**
   * Exposes the Stripe CardCvcElement
   * @return {null|*}
   */
  get cardCvcElement() {
    if (this.elements) {
      return this.elements.getElement(CardCvcElement);
    }
    return null;
  }

  /**
   * Exposes the Stripe CardExpiryElement
   * @return {null|*}
   */
  get cardExpiryElement() {
    if (this.elements) {
      return this.elements.getElement(CardExpiryElement);
    }
    return null;
  }

  /**
   * Exposes the Stripe CardNumberElement
   * @return {null|*}
   */
  get cardNumberElement() {
    if (this.elements) {
      return this.elements.getElement(CardNumberElement);
    }
    return null;
  }

  /**
   * Wraps a Stripe JS API call to confirmCardPayment()
   * @see https://stripe.com/docs/js/payment_intents/confirm_card_payment
   * @param clientSecret
   * @return {*}
   */
  confirmCardPayment({ clientSecret }) {
    return this.stripe.confirmCardPayment(clientSecret);
  }

  /**
   * Wraps a Stripe JS API call to createPaymentMethod()
   * (https://stripe.com/docs/js/payment_methods/create_payment_method),
   * passing in some defaults and also billing details. We expect to be creating
   * a credit card method, hence type: "card" and the card number element.
   * @return {Promise<{paymentMethod?: PaymentMethod; error?: StripeError}> | Error}
   */
  createPaymentMethod() {
    return this.stripe.createPaymentMethod({
      type: "card",
      card: this.cardNumberElement,
      billing_details: this.billingDetails,
    });
  }

  /**
   * Internal interface to expose the current `donationDetails` on the `donationForm`
   * @return {{country: *, lastName: *, amount: *, address: *, city: *,
   *   postcode: *, title: *, giftAid: *, frequency: *, name: *,
   *   recaptchaToken: *, anonymous: boolean, state: *, paypal: boolean,
   *   email: *}}
   */
  get donationDetails() {
    return this.donationForm.current.donationDetails;
  }

  /**
   * Enables or disables all Stripe Elements (card number, expiry and CVV inputs)
   * @param enabled If true, enable all elements; if false, disable all
   */
  enableElements(enabled) {
    const disabled = !enabled;
    if (this.cardNumberElement) { this.cardNumberElement.update({ disabled }); }
    if (this.cardExpiryElement) { this.cardExpiryElement.update({ disabled }); }
    if (this.cardCvcElement) { this.cardCvcElement.update({ disabled }); }
  }

  /**
   * Executes the appropriate payment flow, passing the result to the backend
   * at the appropriate point, and responds to its success or
   * failure by calling appropriate methods (`succeed()` or `fail()`)
   */
  async executePayment() {
    try {
      await this.paymentFlow.executePayment();
      this.succeed();
    } catch (error) {
      this.fail(error);
    }
  }

  /**
   * Internal interface to expose `donationForm`'s `fail()` method, so we can
   *   call it if the payment process fails
   * @param error
   */
  fail(error) {
    this.donationForm.current.fail(error);
  }

  /**
   * Internal interface to expose the current frequency on the `donationForm`
   * @return {*} `"once"` or `"monthly"`
   */
  get frequency() {
    return this.donationForm.current.frequency;
  }

  /**
   * Internal interface to expose the appropriate current payment flow to
   * execute any payments on
   */
  get paymentFlow() {
    if (this.frequency === "monthly") {
      return this.stripeRecurringFlow;
    }
    return this.stripeOnceOffFlow;
  }

  savePaymentIntent(paymentMethod) {
    const { amount, frequency, recaptchaToken } = this.donationDetails;
    const recurring = frequency !== "once";
    return this.stripePaymentIntentManager.save({ amount, paymentMethod, recaptchaToken, recurring });
  }

  /**
   * Submits the donation form to the back-end using `TcDonateGateway`, passing
   *   a merger of `this.donationDetails` and `paymentTokens`
   * @param paymentTokens Stripe tokens which the back-end needs to know about,
   *   either in order to complete payment, or to match to incoming webhooks
   */
  submitForm({ paymentTokens }) {
    const donation = { paymentTokens, ...this.donationDetails };
    return this.tcDonateGateway.call("donations.json", "POST", { donation });
  }

  /**
   * Internal interface to expose `donationForm`'s `succeed()` method, so we can
   *   call it if the payment process succeeds
   */
  succeed() {
    this.donationForm.current.succeed();
  }
}
